import { uniq, uniqBy } from "lodash-es";
import { defineStore, storeToRefs } from "pinia";
import { computed, type Ref, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { getLocale, getLocaleText } from "shared/boot/i18n";
import type { MultiHeaderWidgetOption } from "shared/components/widgets/MultiHeaderWidget.vue";
import useSupportLinking from "shared/composables/useSupportLinking";
import { toStreamFilters } from "shared/helpers/advancedFilters";
import DateRange from "shared/helpers/DateRange";
import {
  getSources,
  getTypesForMedia,
  getTypesForStreams,
} from "shared/helpers/media";
import { safePush } from "shared/helpers/routing";
import { isSocialBookmarkStream, isSocialStream } from "shared/helpers/stream";
import type StreamFilters from "shared/helpers/StreamFilters";
import type { StreamReportFilters } from "shared/helpers/StreamFilters";
import { randomUUID } from "shared/helpers/uuid";
import DialogService from "shared/modals/DialogService";
import ModalService from "shared/modals/ModalService";
import { normalizeChartSetting } from "shared/modules/Charts/helpers/charts";
import {
  type BackendChartConfig,
  type ChartIndex,
  type ChartOption,
  type SavedChartSetting,
} from "shared/modules/Charts/types";
import {
  DEFAULT_SOV_METRIC_TYPE,
  DEFAULT_TOP_ARTICLES_METRIC,
} from "shared/modules/Charts/types";
import {
  type ChartConfig,
  SidebarComponent,
} from "shared/modules/Charts/types/chartConfig";
import {
  type Chart,
  type InstantInsightsChart,
} from "shared/modules/Charts/types/chartTypes";
import { type ChartLibraryProps } from "shared/modules/Charts/types/data";
import {
  hasIntentionalEmptyMentionStreams,
  isChartSettingWithMediaReleases,
  isChartSettingWithMentionStreamsAndSocialStreams,
  isChartSettingWithSocialStreamsOnly,
  isChartSettingWithSortBy,
  isChartSettingWithSovMetricType,
  isChartSettingWithStreamsAndMediaTypes,
} from "shared/modules/Charts/types/guards";
import { useInsightsLandingPageStoreFactory } from "shared/modules/Insights/stores/useInsightsLandingPageStoreFactory";
import {
  InsightsAction,
  type InsightsDashboardError,
  type InsightsDashboardFormData,
  InsightsLandingPageType,
} from "shared/modules/Insights/types";
import { useInsightsDashboardActions } from "shared/modules/InsightsDashboard/composables/dashboard/useInsightsDashboardActions";
import { useInsightsDashboardRoutes } from "shared/modules/InsightsDashboard/composables/dashboard/useInsightsDashboardRoutes";
import { useInsightsDashboardTemplates } from "shared/modules/InsightsDashboard/composables/dashboard-template/useInsightsDashboardTemplates";
import type {
  InsightsDashboardTemplate,
  InsightsDashboardTemplateConfig,
} from "shared/modules/InsightsDashboard/types/dashboardTemplates";
import { useInstantInsights } from "shared/modules/InstantInsights/composables/useInstantInsights";
import { useInstantInsightsCharts } from "shared/modules/InstantInsights/composables/useInstantInsightsCharts";
import {
  InstagramHandlesOrganisationSocialIntegration,
  InstantInsightsReport,
} from "shared/resources";
import createInsightsService from "shared/services/api/InsightsServiceFactory";
import { isAbortError } from "shared/services/api/isAbortError";
import features from "shared/services/features";
import {
  REQUIRED_FEATURES,
  useSocialIntegrationsStore,
} from "shared/stores/socialIntegrations";
import useStreamsStore from "shared/stores/streams";
import useUserStore from "shared/stores/user";
import type { Nullable, Stream, Uuid } from "shared/types";
import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";
import { type SocialAccountOption } from "shared/types/social";

const HIDDEN_PUBLIC_SETTINGS_COMPONENTS = [SidebarComponent.LinkedStreamForm];

interface InstagramHandleOptionsLoadState {
  loaded: Ref<boolean>;
  loadedTeamId: Ref<Nullable<number>>;
  options: Ref<SocialAccountOption[]>;
  promise: Nullable<Promise<void>>;
  promiseTeamId: Nullable<number>;
  requestId: number;
}

export const useInsightsDashboardStore = defineStore(
  "insightsDashboard",
  () => {
    const router = useRouter();
    const route = useRoute();
    const safeRouterPush = safePush({ router });

    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);

    const streamsStore = useStreamsStore();
    const { mentionStreams, socialStreams } = streamsStore;

    const {
      mediaBookmarkStreams,
      socialBookmarkStreams,
      sortedTraditionalStreams,
    } = storeToRefs(streamsStore);

    const socialIntegrationsStore = useSocialIntegrationsStore();

    const { engageTeamIntegrations, socialAccountOptions } = storeToRefs(
      socialIntegrationsStore
    );

    const defaultDashboardRange = DateRange.lastThirtyDays();

    const { loadAvailableCharts, loadMediaReleasesHeaderOptions } =
      useInstantInsights();

    const {
      handleDeleteChart: onDeleteChart,
      handleDuplicateDashboard,
      handleGenerateDashboardPdf,
      handleGenerateDashboardScheduledReport,
      handleGenerateDashboardXlsx,
      handleSaveDashboard: onSaveDashboard,
      handleShareDashboard,
      handleToggleFavoriteDashboard,
      hasOpenConfirmDialog,
      loadingActions,
    } = useInsightsDashboardActions();

    const { buildInsightsDashboardRouteName } = useInsightsDashboardRoutes();

    const isPublicInsights = ref<boolean>(false);

    const isEditable = ref<boolean>(false);
    const isLoading = ref<boolean>(false);
    const isLoadingSocialIntegrations = ref<boolean>(false);
    let pendingSocialIntegrationLoads = 0;
    const notFound = ref<boolean>(false);
    const hasError = ref<boolean>(false);

    const availableCharts = ref<ChartIndex>();
    const availableChartsLocale = ref<string>();
    const mediaReleases = ref<MultiHeaderWidgetOption[]>([]);
    const dashboard = ref<InstantInsightsReport>();
    const dashboardAbortController = ref<AbortController>();
    const chartCacheToken = ref<Uuid>(randomUUID());

    const landingPageType = ref<InsightsLandingPageType>(
      InsightsLandingPageType.Dashboards
    );

    const streamIds: number[] = [];

    if (sortedTraditionalStreams.value?.[0]) {
      streamIds.push(sortedTraditionalStreams.value[0]?.id);
    }

    const initFormData = (): InsightsDashboardFormData => ({
      advancedFilters: null,
      dateRange: defaultDashboardRange,
      description: "",
      newChartSettings: [],
      streamIds,
      syndicationEnabled: false,
      title: "",
    });

    function initFromTemplate(
      template: InsightsDashboardTemplate,
      config: InsightsDashboardTemplateConfig
    ): void {
      const { createChartSettingFromTemplate } =
        useInsightsDashboardTemplates();

      formData.value = {
        ...formData.value,
        aiVisibilityProjectId: config.aiVisibilityProjectId,
        dateRange: config.dateRange,
        newChartSettings: template.charts.map((chartKey) =>
          createChartSettingFromTemplate(chartKey, config)
        ),
        streamIds: config.mentionStreamIds ?? formData.value.streamIds ?? [],
      };

      isEditable.value = true;
    }

    const formData = ref<InsightsDashboardFormData>(initFormData());

    const isFavorite = computed(() => Boolean(dashboard.value?.favorite));

    const allAvailableStreams = computed<Stream[]>(() =>
      uniqBy(
        [
          ...mentionStreams(),
          ...socialStreams(),
          ...mediaBookmarkStreams.value,
          ...socialBookmarkStreams.value,
        ],
        "id"
      )
    );

    const allSharedStreams = computed<Stream[]>(() => {
      const streamsData = dashboard.value?.streamsData || [];

      return streamsData.filter(
        (stream) => !allAvailableStreams.value.includes(stream)
      );
    });

    const sharedSocialStreams = computed<Stream[]>(() =>
      allSharedStreams.value.filter(
        (stream) => isSocialStream(stream) || isSocialBookmarkStream(stream)
      )
    );

    const sharedStreams = computed<Stream[]>(() => {
      const sharedSocialStreamIds = sharedSocialStreams.value.map(
        (stream) => stream.id
      );

      return allSharedStreams.value.filter(
        (stream) => !sharedSocialStreamIds.includes(stream.id)
      );
    });

    const availableSocialStreams = computed<Stream[]>(() =>
      allAvailableStreams.value.filter(
        (stream) => isSocialStream(stream) || isSocialBookmarkStream(stream)
      )
    );

    const availableStreams = computed<Stream[]>(() => {
      const availableSocialStreamIds = availableSocialStreams.value.map(
        (stream) => stream.id
      );

      return allAvailableStreams.value.filter(
        (stream) => !availableSocialStreamIds.includes(stream.id)
      );
    });

    const selectedSocialStreams = computed<Stream[]>(() =>
      availableSocialStreams.value.filter((stream) =>
        formData.value.streamIds.includes(stream.id)
      )
    );

    const availableSocialContent = computed<SocialMentionType[]>(() =>
      Array.from(
        new Set(
          selectedSocialStreams.value
            .flatMap((stream) => stream?.selected_social_content ?? [])
            .filter(Boolean)
        )
      )
    );

    const { chartsIndex } = useInstantInsightsCharts({
      streams: [...allAvailableStreams.value, ...allSharedStreams.value],
    });

    const defaultMentionTypes = computed<MentionType[]>(() =>
      getTypesForMedia(getSources().map((source) => source.medium))
    );

    const defaultEnabledMedia = computed<Record<string, boolean>>(() =>
      getSources().reduce(
        (acc, toggle) =>
          Object.assign(acc, { [`${toggle.field}Content`]: true }),
        {}
      )
    );

    const owner = computed<string>(() => {
      if (!dashboard.value?.id) {
        return `${currentUser.value.first_name} ${currentUser.value.last_name}`;
      }

      return `${dashboard.value?.user?.firstName} ${dashboard.value?.user?.lastName}`;
    });

    const dashboardError = computed<Nullable<InsightsDashboardError>>(() => {
      if (!hasError.value && !notFound.value) {
        return null;
      }

      const titleKey = hasError.value
        ? "insights_dashboard.loading_dashboard_error"
        : "insights_dashboard.dashboard_not_found";

      const descriptionKey = hasError.value
        ? "insights_dashboard.loading_dashboard_error_description"
        : "insights_dashboard.dashboard_not_found_description";

      return {
        buttonLabel: getLocaleText("insights_dashboard.back_to_dashboards"),
        description: getLocaleText(descriptionKey),
        title: getLocaleText(titleKey),
      };
    });

    const dashboardRange = computed<DateRange>(() => {
      if (!dashboard.value) {
        return defaultDashboardRange;
      }

      return (
        dashboard.value.relativeRange() ||
        dashboard.value.absoluteRange() ||
        defaultDashboardRange
      );
    });

    const dashboardMediaReleaseIds = computed<number[]>(() => {
      if (!dashboard.value) {
        return [];
      }

      const chartsWithMediaReleases = dashboard.value.newChartSettings.filter(
        isChartSettingWithMediaReleases
      );

      return uniq(
        chartsWithMediaReleases
          .map((chartSetting) => chartSetting.media_release_ids ?? [])
          .flat()
      );
    });

    const instagramHandleOptions = ref<SocialAccountOption[]>([]);
    const ownedInstagramHandleOptions = ref<SocialAccountOption[]>([]);

    const instagramHandleOptionsLoadState: InstagramHandleOptionsLoadState = {
      loaded: ref<boolean>(false),
      loadedTeamId: ref<Nullable<number>>(null),
      options: instagramHandleOptions,
      promise: null,
      promiseTeamId: null,
      requestId: 0,
    };

    const ownedInstagramHandleOptionsLoadState: InstagramHandleOptionsLoadState =
      {
        loaded: ref<boolean>(false),
        loadedTeamId: ref<Nullable<number>>(null),
        options: ownedInstagramHandleOptions,
        promise: null,
        promiseTeamId: null,
        requestId: 0,
      };

    const currentSocialTeamId = computed<Nullable<number>>(() => {
      if (!userStore.organisation.organisation_teams) {
        return null;
      }

      return features.has(REQUIRED_FEATURES.hasTeamWorkspaces)
        ? (userStore.preferences.selected_team_social ?? null)
        : currentUser.value.primary_organisation_team_id;
    });

    function invalidateInstagramHandleOptions(): void {
      [
        instagramHandleOptionsLoadState,
        ownedInstagramHandleOptionsLoadState,
      ].forEach((state) => {
        state.options.value = [];
        state.loaded.value = false;
        state.loadedTeamId.value = null;
        state.promise = null;
        state.promiseTeamId = null;
        state.requestId += 1;
      });
    }

    watch(currentSocialTeamId, invalidateInstagramHandleOptions, {
      flush: "sync",
    });

    function isLoadingAction(action: InsightsAction): boolean {
      const id: number = Number(dashboard.value?.id) || -1;

      return Boolean(loadingActions.value?.[action]?.includes(id));
    }

    const isSaving = computed<boolean>(() =>
      isLoadingAction(InsightsAction.Save)
    );

    const isSavingFavorite = computed<boolean>(() =>
      isLoadingAction(InsightsAction.Star)
    );

    const availableChartOptions = computed<ChartOption[]>(() =>
      (Object.keys(chartsIndex.value) as InstantInsightsChart[]).map(
        (value) =>
          ({
            label: getDefaultChartLabel(value),
            testId: `chart-option-${value}`,
            value,
          }) satisfies ChartOption
      )
    );

    function buildNewDashboard(): InstantInsightsReport {
      return new InstantInsightsReport({
        after: defaultDashboardRange.after,
        before: defaultDashboardRange.before,
        canModify: true,
        collapseSyndicated: false,
        rangeLabel: defaultDashboardRange.rangeLabel,
        userId: currentUser.value.id,
        ...defaultEnabledMedia.value,
        advancedFilters: null,
        newChartSettings: [],
        selectedCharts: [],
        selectedSocialContent: [],
        streamIds: [sortedTraditionalStreams.value[0]?.id],
      });
    }

    function setDashboard(data: InstantInsightsReport): void {
      if (dashboard.value?.id !== data.id) {
        clearAllChartData();
      }

      dashboard.value = data;

      dashboard.value!.newChartSettings = (
        dashboard.value!.newChartSettings || []
      ).map((chartSetting) => {
        const normalizedChartSetting = normalizeChartSetting(chartSetting);

        const chartVariant =
          normalizedChartSetting?.chart_variant ||
          getDefaultChartSetting(normalizedChartSetting.chart)
            ?.default_chart_variant;

        let { types } = normalizedChartSetting;

        if (types) {
          const streams = getStreamsByIds(
            normalizedChartSetting.stream_ids || []
          );

          const streamTypes = getTypesForStreams(streams);

          types = types.filter((type) => streamTypes.includes(type));
        }

        return {
          ...normalizedChartSetting,
          range: getChartRange(normalizedChartSetting),
          ...(types && { types }),
          ...(chartVariant ? { chart_variant: chartVariant } : {}),
        };
      });
    }

    function setFormData(): void {
      if (!dashboard.value) {
        return;
      }

      const dateRange =
        dashboard.value.relativeRange() ||
        dashboard.value.absoluteRange() ||
        defaultDashboardRange;

      let advancedFilters: Nullable<StreamFilters> = null;

      if (dashboard.value.advancedFilters) {
        advancedFilters = createStreamFiltersFromOptions(
          dashboard.value.advancedFilters
        );
      }

      formData.value = {
        ...formData.value,
        advancedFilters,
        dateRange,
        description: dashboard.value.description || "",
        newChartSettings: dashboard.value.newChartSettings,
        streamIds: dashboard.value.streamIds || formData.value.streamIds,
        syndicationEnabled: dashboard.value.collapseSyndicated,
        title: dashboard.value.name || "",
      };
    }

    function handleEditDashboard(): void {
      setFormData();
      isEditable.value = true;
    }

    function handleBackToDashboards(): void {
      safeRouterPush({
        name: buildInsightsDashboardRouteName({
          type: landingPageType.value,
        }),
      });
    }

    function handleCancelEditDashboard(): void {
      if (!dashboard.value?.id) {
        handleBackToDashboards();

        return;
      }

      setFormData();
      isEditable.value = false;
    }

    async function handleLoadMediaReleases(): Promise<void> {
      try {
        mediaReleases.value = await loadMediaReleasesHeaderOptions(
          dashboardMediaReleaseIds.value
        );
      } catch (error) {
        ErrorDialog(
          getLocaleText("insights_dashboard.loading_media_releases_error"),
          error
        );
      }
    }

    async function handleLoadSocialIntegrations(): Promise<void> {
      try {
        pendingSocialIntegrationLoads += 1;
        isLoadingSocialIntegrations.value = true;
        await socialIntegrationsStore.loadIntegrations();
      } catch (error) {
        ErrorDialog(
          getLocaleText("insights_dashboard.loading_social_integrations_error"),
          error
        );
      } finally {
        pendingSocialIntegrationLoads -= 1;
        isLoadingSocialIntegrations.value = pendingSocialIntegrationLoads > 0;
      }
    }

    function loadInstagramHandleOptions(
      owned: boolean,
      state: InstagramHandleOptionsLoadState
    ): Promise<void> {
      const teamId = currentSocialTeamId.value;

      if (state.loaded.value && state.loadedTeamId.value === teamId) {
        return Promise.resolve();
      }

      if (state.promise && state.promiseTeamId === teamId) {
        return state.promise;
      }

      state.requestId += 1;
      const { requestId } = state;

      const fetchInstagramHandleOptions = async (): Promise<void> => {
        try {
          pendingSocialIntegrationLoads += 1;
          isLoadingSocialIntegrations.value = true;
          await socialIntegrationsStore.loadIntegrations();

          const response =
            await InstagramHandlesOrganisationSocialIntegration.where({
              owned,
            })
              .includes(["instagramHandle", "organisationSocialIntegration"])
              .order({ username: "asc" })
              .per(999)
              .all();

          const teamIntegrationIds = new Set(
            engageTeamIntegrations.value
              .map((integration) => integration.id)
              .filter((id): id is string => id !== undefined)
              .map(String)
          );

          const handleOptions = response.data
            .filter(
              (connection) =>
                !connection.archivedAt &&
                connection.id &&
                connection.instagramHandle?.username &&
                connection.organisationSocialIntegration?.id &&
                teamIntegrationIds.has(
                  String(connection.organisationSocialIntegration.id)
                )
            )
            .map((connection) => ({
              label: connection.instagramHandle.username,
              platformIcon:
                connection.organisationSocialIntegration.platformIcon(),
              socialPlatform: "InstagramSocialIntegration",
              value: String(connection.id),
            }));

          if (
            requestId !== state.requestId ||
            teamId !== currentSocialTeamId.value
          ) {
            return;
          }

          state.options.value = handleOptions;
          state.loaded.value = true;
          state.loadedTeamId.value = teamId;
        } catch (error) {
          ErrorDialog(
            getLocaleText(
              "insights_dashboard.loading_social_integrations_error"
            ),
            error
          );
        } finally {
          pendingSocialIntegrationLoads -= 1;
          isLoadingSocialIntegrations.value = pendingSocialIntegrationLoads > 0;

          if (requestId === state.requestId) {
            state.promise = null;
            state.promiseTeamId = null;
          }
        }
      };

      state.promise = fetchInstagramHandleOptions();
      state.promiseTeamId = teamId;

      return state.promise;
    }

    function handleLoadInstagramHandleOptions(): Promise<void> {
      return loadInstagramHandleOptions(false, instagramHandleOptionsLoadState);
    }

    function handleLoadOwnedInstagramHandleOptions(): Promise<void> {
      return loadInstagramHandleOptions(
        true,
        ownedInstagramHandleOptionsLoadState
      );
    }

    async function handleLoadDashboard(
      id?: number | string,
      options: { editing: boolean; publicAccessKey?: string } = {
        editing: false,
      }
    ): Promise<void> {
      try {
        if (dashboardAbortController.value) {
          dashboardAbortController.value.abort();
        }

        dashboardAbortController.value = new AbortController();

        isLoading.value = true;
        notFound.value = false;
        hasError.value = false;

        const currentLocale = getLocale();

        if (
          !availableCharts.value ||
          availableChartsLocale.value !== currentLocale
        ) {
          availableCharts.value = await loadAvailableCharts({
            publicAccessKey: options.publicAccessKey,
          });

          availableChartsLocale.value = currentLocale;
        }

        if (!options.publicAccessKey) {
          await handleLoadSocialIntegrations();
        }

        if (!id) {
          dashboard.value = buildNewDashboard();

          handleEditDashboard();

          return;
        }

        const service = createInsightsService({
          publicAccessKey: options.publicAccessKey,
        });

        const data = await service.dashboard(
          options.publicAccessKey ? undefined : Number(id),
          { signal: dashboardAbortController.value?.signal }
        );

        if (!data) {
          notFound.value = true;

          return;
        }

        if (data.aiVisibilityProject) {
          track(
            "AI Visibility - Viewing dashboard with AI Visibility connected",
            {
              id: data.id,
            }
          );
        }

        setDashboard(data);

        if (options.editing) {
          handleEditDashboard();
        }
      } catch (error: unknown) {
        if (isAbortError(error)) {
          return;
        }

        hasError.value = true;

        ErrorDialog(
          getLocaleText("insights_dashboard.loading_dashboard_error"),
          error
        );
      } finally {
        isLoading.value = false;
      }
    }

    async function handleSaveDashboard(
      skipConfirmDialog: boolean = false
    ): Promise<boolean> {
      if (!dashboard.value) {
        return false;
      }

      const isCreating: boolean = !dashboard.value.id;

      const saved = await onSaveDashboard(
        dashboard.value,
        formData.value,
        skipConfirmDialog
      );

      if (saved) {
        setDashboard(dashboard.value);
        isEditable.value = false;

        if (isCreating) {
          // Refresh recent dashboards list when a new dashboard is created
          const dashboardsLandingPageStore = useInsightsLandingPageStoreFactory(
            InsightsLandingPageType.Dashboards
          )();

          dashboardsLandingPageStore.handleLoadLandingPageItems();

          safeRouterPush({
            name: (route.name! as string).replace("-new", ""),
            params: {
              ...route.params,
              id: dashboard.value.id,
            },
          });
        }
      }

      return saved;
    }

    async function handleDeleteChart(
      chart: SavedChartSetting
    ): Promise<boolean> {
      if (!dashboard.value) {
        return Promise.resolve(false);
      }

      const deleted = await onDeleteChart(dashboard.value, {
        ...chart,
        label: getChartLabel(chart),
      });

      if (deleted) {
        setDashboard(dashboard.value);
      }

      return deleted;
    }

    function handleAction(action: InsightsAction): Promise<boolean> {
      if (!dashboard.value) {
        return Promise.resolve(false);
      }

      switch (action) {
        case InsightsAction.Duplicate:
          return handleDuplicateDashboard(dashboard.value);
        case InsightsAction.GeneratePDF:
          return handleGenerateDashboardPdf(
            dashboard.value,
            availableCharts.value
          );
        case InsightsAction.GenerateScheduledReport:
          return handleGenerateDashboardScheduledReport(dashboard.value);
        case InsightsAction.GenerateXlsx:
          return handleGenerateDashboardXlsx(
            dashboard.value,
            availableCharts.value
          );
        case InsightsAction.Share:
          return handleShareDashboard(dashboard.value);
        case InsightsAction.Star:
          return handleToggleFavoriteDashboard(dashboard.value);
        case InsightsAction.Unstar:
          return handleToggleFavoriteDashboard(dashboard.value);
        case InsightsAction.Save:
          return handleSaveDashboard();
        case InsightsAction.SaveChart:
          return handleSaveDashboard(true);
        case InsightsAction.AIVisibilityProject:
          handleAiVisibilityProjectAction();

          return Promise.resolve(false);
        default:
          return Promise.resolve(false);
      }
    }

    function handleAiVisibilityProjectAction() {
      if (features.has("has_ai_visibility")) {
        ModalService.open("AIVisibilityInsightsModal");

        return;
      }

      const { mailTo } = useSupportLinking();

      DialogService.confirm({
        ...(mailTo.value
          ? {
              confirmButtonProps: {
                href: mailTo.value,
                type: "a",
              },
              confirmLabel: getLocaleText("upsell_card.get_in_touch"),
            }
          : {}),
        message: getLocaleText("upsell_card.ai_visibility.description"),
        title: getLocaleText("upsell_card.ai_visibility.title"),
      });
    }

    function getDefaultStreams(chartSetting?: SavedChartSetting): Stream[] {
      const supportsSocialOnly =
        isChartSettingWithSocialStreamsOnly(chartSetting);

      const supportsSocialOptional =
        isChartSettingWithMentionStreamsAndSocialStreams(chartSetting);

      let streams: Stream[] = [
        ...availableStreams.value,
        ...sharedStreams.value,
      ];

      if (!chartSetting || supportsSocialOptional) {
        streams = [...allAvailableStreams.value, ...allSharedStreams.value];
      } else if (supportsSocialOnly) {
        streams = [
          ...availableSocialStreams.value,
          ...sharedSocialStreams.value,
        ];
      }

      const uniqueStreams = uniqBy(streams, "id");

      // Sort streams so that IDs in formData.streamIds appear first
      const selectedStreamIds = isEditable.value
        ? (formData.value?.streamIds ?? [])
        : (dashboard.value?.streamIds ?? []);

      if (selectedStreamIds.length > 0) {
        return uniqueStreams.sort((streamA, streamB) => {
          const streamAIsSelected = selectedStreamIds.includes(streamA.id);
          const streamBIsSelected = selectedStreamIds.includes(streamB.id);

          if (streamAIsSelected && !streamBIsSelected) return -1;

          if (!streamAIsSelected && streamBIsSelected) return 1;

          return 0;
        });
      }

      return uniqueStreams;
    }

    function getDefaultStream(
      chartSetting?: SavedChartSetting
    ): Nullable<Stream> {
      const streams = getDefaultStreams(chartSetting);

      if (!streams.length) {
        return null;
      }

      const supportsSocialOnly =
        isChartSettingWithSocialStreamsOnly(chartSetting);

      const supportsSocialOptional =
        isChartSettingWithMentionStreamsAndSocialStreams(chartSetting);

      const streamId = isEditable.value
        ? formData.value?.streamIds?.[0]
        : dashboard.value?.streamIds?.[0];

      let stream = null;

      if (streamId) {
        stream = streams.find((str) => str.id === streamId);
        const isSocialStreamSelected = stream && isSocialStream(stream);

        const violatesSocialOnly =
          supportsSocialOnly && !isSocialStreamSelected;

        const violatesMentionOnly =
          !supportsSocialOptional && isSocialStreamSelected;

        if (chartSetting && (violatesSocialOnly || violatesMentionOnly)) {
          stream = null;
        }
      }

      if (!stream) {
        stream = streams.find((str) => str.primary);
      }

      if (!stream) {
        [stream] = streams;
      }

      return stream;
    }

    function getCompositeStream(
      chartSetting?: SavedChartSetting
    ): Nullable<Stream> {
      if (hasIntentionalEmptyMentionStreams(chartSetting)) {
        return null;
      }

      const baseStream = getDefaultStream(chartSetting);

      if (
        !baseStream ||
        !chartSetting ||
        !isChartSettingWithStreamsAndMediaTypes(chartSetting)
      ) {
        return baseStream;
      }

      const chartStreams = getStreamsByIds(chartSetting.stream_ids ?? []);

      if (!chartStreams.length) {
        return baseStream;
      }

      if (chartStreams.length === 1) {
        return chartStreams[0];
      }

      // When there are multiple chart streams, prefer the dashboard's selected
      // stream if it's one of the chart's configured streams. This allows
      // filter updates to propagate when users change the dashboard stream.
      // Otherwise, use the chart's first configured stream.
      const dashboardStreamInChart = chartStreams.find(
        (stream) => stream.id === baseStream?.id
      );

      const primaryChartStream = dashboardStreamInChart ?? chartStreams[0];

      const mergedMedia = uniq(
        chartStreams.flatMap((stream) => stream.enabled_media || [])
      );

      const mergedSocialContent = uniq(
        chartStreams.flatMap((stream) => stream.selected_social_content || [])
      );

      const mergedTvContent = uniq(
        chartStreams.flatMap((stream) => stream.selected_tv_content || [])
      );

      return {
        ...primaryChartStream,
        enabled_media:
          mergedMedia.length > 0
            ? mergedMedia
            : primaryChartStream.enabled_media,
        selected_social_content:
          mergedSocialContent.length > 0
            ? mergedSocialContent
            : primaryChartStream.selected_social_content,
        selected_tv_content:
          mergedTvContent.length > 0
            ? mergedTvContent
            : primaryChartStream.selected_tv_content,
      };
    }

    function createStreamFiltersFromOptions(
      options: StreamReportFilters,
      chartSetting?: SavedChartSetting
    ): StreamFilters {
      const stream = getCompositeStream(chartSetting);

      return toStreamFilters(options, stream);
    }

    const chartDataMap = ref<Record<Uuid, ChartLibraryProps>>({});
    const lastViewedChartUuid = ref<Nullable<Uuid>>(null);

    function setLastViewedChartUuid(uuid: Nullable<Uuid>): void {
      lastViewedChartUuid.value = uuid;
    }

    function setChartData(uuid: Uuid, data: ChartLibraryProps): void {
      chartDataMap.value[uuid] = {
        ...data,
        options: { ...data.options, animation: false },
      };
    }

    function getChartData(uuid: Uuid): Nullable<ChartLibraryProps> {
      return chartDataMap.value[uuid] ?? null;
    }

    function clearChartData(uuid: Uuid): void {
      delete chartDataMap.value[uuid];
    }

    function clearAllChartData(): void {
      chartDataMap.value = {};
    }

    function handleResetStore(): void {
      clearAllChartData();
      invalidateInstagramHandleOptions();

      formData.value = initFormData();
      lastViewedChartUuid.value = null;
      dashboard.value = undefined;
      isEditable.value = false;
      isLoading.value = false;
      notFound.value = false;
      hasError.value = false;
    }

    function getChartConfig(chart: Chart): Nullable<ChartConfig> {
      const chartConfig = chartsIndex.value[chart];

      if (!chartConfig) {
        return null;
      }

      return chartConfig;
    }

    function getChartSettingByUuid(uuid: Uuid): Nullable<SavedChartSetting> {
      return (
        dashboard.value?.newChartSettings?.find(
          (chartSetting) => chartSetting.uuid === uuid
        ) ?? null
      );
    }

    function getDefaultChartSetting(
      chart: Chart
    ): Nullable<BackendChartConfig> {
      if (!availableCharts.value) {
        return null;
      }

      return availableCharts.value[chart] || null;
    }

    function getStreamsByIds(ids: number[]): Stream[] {
      return ids
        .map(getStreamById)
        .filter((stream): stream is Stream => Boolean(stream));
    }

    function getDefaultChartStream(
      chartSetting: SavedChartSetting
    ): Nullable<Stream> {
      return getCompositeStream(chartSetting);
    }

    function getChartSettingsComponent(
      chartSetting: SavedChartSetting
    ): Nullable<SidebarComponent> {
      const settingsComponent =
        chartsIndex.value[chartSetting.chart]?.settingsComponent;

      if (
        isPublicInsights.value &&
        settingsComponent &&
        HIDDEN_PUBLIC_SETTINGS_COMPONENTS.includes(settingsComponent)
      ) {
        return null;
      }

      return chartsIndex.value[chartSetting.chart]?.settingsComponent || null;
    }

    function getChartByUuid(uuid: Uuid): SavedChartSetting | undefined {
      const chart = dashboard.value?.newChartSettings.find(
        (chartSetting) => chartSetting.uuid === uuid
      );

      if (!chart) {
        return undefined;
      }

      return { ...chart, label: getChartLabel(chart) };
    }

    function getDefaultChartLabel(chart: Chart): string {
      const defaultChartSetting = getDefaultChartSetting(chart);
      const chartConfig = getChartConfig(chart);

      return defaultChartSetting?.label || chartConfig?.label || "";
    }

    function getChartLabel(chartSetting: SavedChartSetting): string {
      return chartSetting.label || getDefaultChartLabel(chartSetting.chart);
    }

    function getResolvedChartLabel(chartSetting: SavedChartSetting): string {
      const base = getChartLabel(chartSetting);
      const defaultSetting = getDefaultChartSetting(chartSetting.chart);

      const hasCustomLabel =
        chartSetting.label && chartSetting.label !== defaultSetting?.label;

      if (!hasCustomLabel && defaultSetting?.metric_labels) {
        if (isChartSettingWithSovMetricType(chartSetting)) {
          const metricType =
            chartSetting.metric_type || DEFAULT_SOV_METRIC_TYPE;

          const metricLabel = defaultSetting.metric_labels[metricType];
          if (metricLabel) return `${base} - ${metricLabel}`;
        }

        if (isChartSettingWithSortBy(chartSetting)) {
          const sortBy = chartSetting.sort_by || DEFAULT_TOP_ARTICLES_METRIC;
          const metricLabel = defaultSetting.metric_labels[sortBy];
          if (metricLabel) return `${base} - ${metricLabel}`;
        }
      }

      return base;
    }

    function getChartLabelByUuid(uuid: Uuid): string {
      const chartSetting = getChartSettingByUuid(uuid);

      if (!chartSetting) {
        return "";
      }

      return getResolvedChartLabel(chartSetting);
    }

    function getChartDescription(chartSetting: SavedChartSetting): string {
      const defaultChartSetting = getDefaultChartSetting(chartSetting.chart);

      return (
        chartSetting.info_tooltip || defaultChartSetting?.info_tooltip || ""
      );
    }

    function getChartDescriptionByUuid(uuid: Uuid): string {
      const chartSetting = getChartSettingByUuid(uuid);

      if (!chartSetting) {
        return "";
      }

      return getChartDescription(chartSetting);
    }

    function getChartRange(chartSetting: SavedChartSetting): DateRange {
      if (chartSetting.range?.range?.label) {
        return DateRange.rangeForLabel(chartSetting.range.range.label);
      }

      if (chartSetting.range?.before && chartSetting.range?.after) {
        return new DateRange({
          after: chartSetting.range.after,
          before: chartSetting.range.before,
        });
      }

      return dashboardRange.value;
    }

    function getStreamById(id: number): Stream | undefined {
      const allKnownStreams = uniqBy(
        [...allAvailableStreams.value, ...(dashboard.value?.streamsData || [])],
        "id"
      );

      return allKnownStreams.find((stream) => stream.id === id);
    }

    function getChartStreams(chartSetting: SavedChartSetting): Stream[] {
      if (
        !isChartSettingWithStreamsAndMediaTypes(chartSetting) ||
        chartSetting.hide_streams
      ) {
        return [];
      }

      return getStreamsByIds(chartSetting.stream_ids);
    }

    function generateChartCacheToken(): void {
      chartCacheToken.value = randomUUID();
    }

    return {
      // State
      availableChartOptions,
      availableCharts,
      chartCacheToken,
      chartDataMap,
      dashboard,
      defaultDashboardRange,
      formData,
      hasError,
      hasOpenConfirmDialog,
      isEditable,
      isLoading,
      isLoadingSocialIntegrations,
      isPublicInsights,
      landingPageType,
      lastViewedChartUuid,
      loadingActions,
      mediaReleases,
      notFound,

      // Getters
      allSharedStreams,
      availableSocialContent,
      availableStreams,
      dashboardError,
      dashboardMediaReleaseIds,
      dashboardRange,
      defaultMentionTypes,
      getDefaultStream,
      getDefaultStreams,
      instagramHandleOptions,
      isFavorite,
      isSaving,
      isSavingFavorite,
      ownedInstagramHandleOptions,
      owner,
      sharedSocialStreams,
      sharedStreams,
      socialAccountOptions,

      // Actions
      clearAllChartData,
      clearChartData,
      generateChartCacheToken,
      getChartByUuid,
      getChartConfig,
      getChartData,
      getChartDescription,
      getChartDescriptionByUuid,
      getChartLabel,
      getChartLabelByUuid,
      getChartRange,
      getChartSettingByUuid,
      getChartSettingsComponent,
      getChartStreams,
      getDefaultChartSetting,
      getDefaultChartStream,
      getResolvedChartLabel,
      getStreamById,
      handleAction,
      handleBackToDashboards,
      handleCancelEditDashboard,
      handleDeleteChart,
      handleEditDashboard,
      handleLoadDashboard,
      handleLoadInstagramHandleOptions,
      handleLoadMediaReleases,
      handleLoadOwnedInstagramHandleOptions,
      handleLoadSocialIntegrations,
      handleResetStore,
      initFromTemplate,
      setChartData,
      setDashboard,
      setLastViewedChartUuid,
    };
  }
);

export default useInsightsDashboardStore;
